import {
   combineReducers,
   configureStore,
   isRejectedWithValue,
   Middleware,
} from "@reduxjs/toolkit"
import { HttpCodes } from "@/constants/globals"
import alarmSchedulesReducer, {
   initialState as initialAlarmSchedulesState,
} from "@/store/alarmSchedulesSlice"
import alertConfigurationsReducer, {
   initialState as initialAlertConfigurationsState,
} from "@/store/alertConfigurationsSlice"
import alertsReducer, {
   initialState as initialAlertsState,
} from "@/store/alertsSlice"
import audioPlaybackReducer, {
   initialState as initialAudioPlaybackState,
} from "@/store/audioPlaybackSlice"
import authReducer, {
   initialState as initialAuthState,
   LOGOUT_FULFILLED,
   RESET_AUTH_STATE,
   resetAuthState,
} from "@/store/authSlice"
import cellsReducer, {
   initialState as initialCellsState,
} from "@/store/cellsSlice"
import customersReducer, {
   initialState as initialCustomersState,
} from "@/store/customersSlice"
import facilitiesReducer, {
   initialState as initialFacilitiesState,
} from "@/store/facilitiesSlice"
import facilityLevelsReducer, {
   initialState as initialFacilityLevelsState,
} from "@/store/facilityLevelsSlice"
import inmatesByLevelReducer, {
   initialState as initialInmatesByLevelState,
} from "@/store/inmatesByLevelSlice"
import inmatesReducer, {
   initialState as initialInmatesState,
} from "@/store/inmatesSlice"
import levelConfigurationsReducer, {
   initialState as initialLevelConfigurationsState,
} from "@/store/levelConfigurationsSlice"
import logsReducer, {
   initialState as initialLogsState,
} from "@/store/logsSlice"
import scannerReducer, {
   initialState as initialScannerState,
} from "@/store/scannerSlice"
import sensorsReducer, {
   initialState as initialSensorsState,
} from "@/store/sensorsSlice"
import unknownLocationWearablesReducer, {
   initialState as initialUnknownLocationWearablesState,
} from "@/store/unknownLocationWearablesSlice"
import userReducer, {
   initialState as initialUserState,
} from "@/store/userSlice"
import usersReducer, {
   initialState as initialUsersState,
} from "@/store/usersSlice"
import wearableSensorsReducer, {
   initialState as initialWearableSensorsState,
} from "@/store/wearableSensorsSlice"
import wearablesRealtimeReducer, {
   initialState as initialWearablesRealtimeState,
} from "@/store/wearablesRealtimeSlice"
import websocketReducer, {
   initialState as initialWebsocketState,
} from "@/store/websocketSlice"

export const unauthorizedMiddleware: Middleware =
   ({ dispatch }) =>
   (next) =>
   (action) => {
      if (
         isRejectedWithValue(action) &&
         action.payload === HttpCodes.UNAUTHORIZED
      ) {
         dispatch(resetAuthState())
      }
      return next(action)
   }

const appReducer = combineReducers({
   alarmSchedulesReducer,
   alertConfigurationsReducer,
   alertsReducer,
   audioPlaybackReducer,
   authReducer,
   cellsReducer,
   customersReducer,
   facilitiesReducer,
   facilityLevelsReducer,
   inmatesByLevelReducer,
   inmatesReducer,
   levelConfigurationsReducer,
   logsReducer,
   scannerReducer,
   sensorsReducer,
   unknownLocationWearablesReducer,
   userReducer,
   usersReducer,
   websocketReducer,
   wearableSensorsReducer,
   wearablesRealtimeReducer,
})

const rootReducer = (
   state: ReturnType<typeof appReducer> | undefined,
   action: { type: string }
) => {
   if ([LOGOUT_FULFILLED, RESET_AUTH_STATE].includes(action.type)) {
      state = {
         alarmSchedulesReducer: initialAlarmSchedulesState,
         alertConfigurationsReducer: initialAlertConfigurationsState,
         alertsReducer: initialAlertsState,
         audioPlaybackReducer: initialAudioPlaybackState,
         authReducer: initialAuthState,
         cellsReducer: initialCellsState,
         customersReducer: initialCustomersState,
         facilitiesReducer: initialFacilitiesState,
         facilityLevelsReducer: initialFacilityLevelsState,
         inmatesByLevelReducer: initialInmatesByLevelState,
         inmatesReducer: initialInmatesState,
         levelConfigurationsReducer: initialLevelConfigurationsState,
         logsReducer: initialLogsState,
         scannerReducer: initialScannerState,
         sensorsReducer: initialSensorsState,
         unknownLocationWearablesReducer: initialUnknownLocationWearablesState,
         userReducer: initialUserState,
         usersReducer: initialUsersState,
         websocketReducer: initialWebsocketState,
         wearableSensorsReducer: initialWearableSensorsState,
         wearablesRealtimeReducer: initialWearablesRealtimeState,
      }
   }
   return appReducer(state, action)
}

export const store = configureStore({
   reducer: rootReducer,
   middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
         serializableCheck: false,
      }).concat(unauthorizedMiddleware)
   },
   devTools: process.env.NODE_ENV !== "production",
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
